import { HelpTooltip } from '../../../shared/components/HelpTooltip';
import type { DeclaredTaxFormState, DeclaredTaxIssues } from '../declaredTaxProfile';
import { DECLARED_TAX_CELL_KEYS, declaredTaxCellLabels, type DeclaredTaxCell, type DeclaredTaxTreatment } from '../types';
import '../benefits-deductions.css';

export const DECLARED_TAX_REVIEW_NOTE =
  'The tax treatment you choose here is saved as “needs review”. It is recorded for review and is not applied to payroll until the treatment is approved.';
export const PRE_TAX_WARNING =
  'Pre-tax does not mean exempt from every tax. The right treatment depends on the benefit plan and the law that applies, so decide each tax yourself: nothing is selected for you.';
export const POST_TAX_NOTE =
  'A post-tax item is generally taken after the applicable taxes are calculated, unless an approved specialized rule says otherwise.';

const TREATMENT_HELP =
  'Pre-tax is taken from pay before taxes are figured; post-tax is taken after. This only records your choice for review; it does not change any calculation.';
const EXEMPTION_HELP =
  'For each tax, say whether this item is exempt from it or still taxable. If you are not sure, ask your payroll or tax professional before saving.';

interface Props {
  state: DeclaredTaxFormState;
  onChange: (next: DeclaredTaxFormState) => void;
  issues: DeclaredTaxIssues;
  /** Show the issues (after a save attempt, or a wrong value). While the person is still filling the form in, only the required marks show. */
  showIssues: boolean;
  disabled?: boolean | undefined;
  /** A prefix that keeps the radio names and ids of two forms apart. */
  idPrefix: string;
}

const TREATMENTS: { value: DeclaredTaxTreatment; label: string }[] = [
  { value: 'PreTax', label: 'Pre-tax' },
  { value: 'PostTax', label: 'Post-tax' },
];

/**
 * The required tax treatment of a custom benefit or deduction, and, for a Pre-tax item, the Admin's own choice for each of five taxes. Nothing is preselected and
 * "Pre-tax" is never taken to mean exempt from everything. What is chosen here is only recorded, as "needs review" (contract 4.1.1.1 and 4.2.1).
 */
export function DeclaredTaxFields({ state, onChange, issues, showIssues, disabled, idPrefix }: Props) {
  const treatmentError = showIssues ? issues.treatment : undefined;
  const cellsError = showIssues ? issues.cells : undefined;

  function chooseTreatment(treatment: DeclaredTaxTreatment) {
    // Post-tax records no per-tax choice, so a Pre-tax choice made earlier is dropped rather than kept out of sight.
    onChange({ treatment, cells: treatment === 'PostTax' ? { federalIncomeTax: '', socialSecurity: '', medicare: '', stateIncomeTax: '', localIncomeTax: '' } : state.cells });
  }

  return (
    <div className="bd-declared" id={idPrefix} tabIndex={-1}>
      <fieldset className="bd-declared__fieldset" disabled={disabled}>
        <legend>
          Tax treatment <span className="jr-req" aria-hidden="true">*</span><span className="jr-sr-only"> (required)</span>{' '}
          <HelpTooltip label="About tax treatment" text={TREATMENT_HELP} />
        </legend>
        <div className="bd-declared__choices" role="radiogroup" aria-label="Tax treatment" aria-required="true" aria-invalid={treatmentError ? true : undefined}>
          {TREATMENTS.map((t) => (
            <label key={t.value} className="bd-declared__choice">
              <input type="radio" name={`${idPrefix}-treatment`} value={t.value} checked={state.treatment === t.value} onChange={() => chooseTreatment(t.value)} />
              {t.label}
            </label>
          ))}
        </div>
        {treatmentError && <p className="jr-error" role="alert">{treatmentError}</p>}
      </fieldset>

      {state.treatment === 'PreTax' && (
        <fieldset className="bd-declared__fieldset" disabled={disabled}>
          <legend>
            Tax exemptions <span className="jr-req" aria-hidden="true">*</span><span className="jr-sr-only"> (choose one for each tax)</span>{' '}
            <HelpTooltip label="About tax exemptions" text={EXEMPTION_HELP} />
          </legend>
          <p className="jr-hint bd-declared__warning" role="note">{PRE_TAX_WARNING}</p>
          <ul className="bd-declared__cells">
            {DECLARED_TAX_CELL_KEYS.map((key) => (
              <li key={key} className="bd-declared__cell">
                <span id={`${idPrefix}-${key}-label`} className="bd-declared__cell-label">{declaredTaxCellLabels[key]}</span>
                <div className="bd-declared__choices" role="radiogroup" aria-labelledby={`${idPrefix}-${key}-label`}>
                  {(['Exempt', 'Taxable'] as DeclaredTaxCell[]).map((cell) => (
                    <label key={cell} className="bd-declared__choice">
                      <input
                        type="radio"
                        name={`${idPrefix}-${key}`}
                        value={cell}
                        checked={state.cells[key] === cell}
                        onChange={() => onChange({ ...state, cells: { ...state.cells, [key]: cell } })}
                      />
                      {cell}
                    </label>
                  ))}
                </div>
              </li>
            ))}
          </ul>
          {cellsError && <p className="jr-error" role="alert">{cellsError}</p>}
        </fieldset>
      )}

      {state.treatment === 'PostTax' && <p className="jr-hint" role="note">{POST_TAX_NOTE}</p>}
      <p className="jr-hint" role="note">{DECLARED_TAX_REVIEW_NOTE}</p>
    </div>
  );
}
